/**
 * Which bubble does an assistant reply belong to?
 *
 * A chat turn puts an empty "loading" bubble on screen and fills it when the
 * answer arrives. Finding that bubble as *"the last message, if it is still
 * loading"* is right exactly until something else finishes the turn first:
 *
 *   * the HARD deadline fires at 180 s, writes "taking longer than expected"
 *     into the bubble and sets `isLoading: false` — so a reply arriving at 200 s
 *     matched neither test and was APPENDED. The person saw the timeout, and
 *     then the answer, as two bubbles.
 *   * by then they may also have asked again, so "the last message" is a
 *     DIFFERENT question's bubble, and writing the late answer there puts it
 *     under a question that did not ask it.
 *
 * So the turn names its bubble, and only that bubble can be answered. Once
 * something has answered it — the reply, the timeout, or a send failure — the
 * turn is over and a straggler has nowhere to land, so it is dropped. First
 * answer wins.
 *
 * Dropping a late reply loses an answer the person could have had. That is the
 * intended trade: they were told the assistant gave up and to try again, and an
 * answer appearing under that sentence — or under their next question — is worse
 * than no answer at all.
 *
 * Structural and generic so the page's `ChatMessage` type stays in the page;
 * pure so the rule can be tested without rendering the page.
 */

/** Where the bubble awaiting an answer sits, or -1 if nothing is awaiting one. */
export function waitingBubbleIndex<T extends { id: string }>(
  bubbles: readonly T[],
  waitingId: string | null,
): number {
  if (!waitingId) return -1;
  return bubbles.findIndex((bubble) => bubble.id === waitingId);
}

/**
 * `patch` applied to the bubble awaiting an answer.
 *
 * Returns the original array — the same reference, so React re-renders nothing —
 * when no bubble is awaiting one.
 */
export function patchWaitingBubble<T extends { id: string }>(
  bubbles: T[],
  waitingId: string | null,
  patch: Partial<T>,
): T[] {
  const index = waitingBubbleIndex(bubbles, waitingId);
  if (index < 0) return bubbles;

  const updated = [...bubbles];
  updated[index] = { ...updated[index], ...patch };
  return updated;
}
